import React, { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';

import { Typography, Section, SidePage, Divider } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import { Conditions } from 'scenes/SmartRules/components/Conditions';
import { DynamicAdvertisingTargetDetails } from 'scenes/SmartRules/components/DynamicAdvertisingTargetDetails';
import {
  useSmartRuleDetails,
  useIsUpdating,
  useSmartRuleActionsLog,
} from 'scenes/SmartRules/data';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';
import InfluencerCampaignDetails from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerCampaignDetails';
import { Actions } from '../components/Actions';
import { useInspection } from '../hooks';
import { ActionsLog } from './ActionsLog';
import { ActionsLogFilter } from './ActionsLog/ActionsLogFilter';
import { actionsLogSchema } from './ActionsLog/schema';
import General from './General';
import Metadata from './Metadata';
import {
  CONDITION_TYPE,
  CONDITIONS,
  DYNAMIC,
  DYNAMIC_CONDITIONS_TARGET,
} from './constants';

const TAB_DETAILS = 'Details';
export const TAB_ACTIONS_LOG = 'Actions Log';

const SmartRuleDetails = ({
  id,
  onClose,
  onExited,
  open,
  activeTabId = TAB_DETAILS,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();

  const { data, isLoading, isRefetching } = useSmartRuleDetails({ id });
  const isUpdating = useIsUpdating();

  const [filters, setFilters] = React.useState(actionsLogSchema.default());
  const { actions, actionsBy, period } = filters;
  const {
    data: dataActionsLog,
    isLoading: isLoadingActionsLog,
    isFetching: isFetchingActionsLog,
    fetchNextPage,
    hasNextPage,
  } = useSmartRuleActionsLog({
    id,
    actions,
    actionsBy,
    period,
  });

  const conditionsCount = data?.[CONDITIONS]?.length || 0;
  const isInspection = useInspection({ data });

  const hasConditionDynamic = useMemo(
    () =>
      data?.conditions.some(
        ({ [CONDITION_TYPE]: conditionType }) => conditionType === DYNAMIC
      ),
    [data?.conditions]
  );

  const loading = isLoading || isUpdating;
  const isDynamicVisible =
    !isString(data?.[DYNAMIC_CONDITIONS_TARGET]) &&
    !isEmpty(data?.[DYNAMIC_CONDITIONS_TARGET]);

  const handleOpenMasterCampaign = (id) => {
    openChildComponent(MasterCampaignDetails, { id });
  };

  const handleOpenInfluencerCampaigns = (id) => {
    openChildComponent(InfluencerCampaignDetails, { id });
  };

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={loading}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Smart Rule'}
        entityId={id}
        subTitle={data?.name}
      />
      <SidePage.Tabs activeTabId={activeTabId} fetching={isRefetching}>
        <SidePage.Tab label={TAB_DETAILS}>
          <SidePage.NewBody wrapper={true} dense={true}>
            <SidePage.NewContent withSidebar={true}>
              <Section title={'General'}>
                <General
                  data={data}
                  onOpenMasterCampaign={handleOpenMasterCampaign}
                  onOpenInfluencerCampaigns={handleOpenInfluencerCampaigns}
                />
              </Section>
              <Section
                title={`Conditions (${conditionsCount})`}
                isCollapsable={true}
                initialOpen={!!conditionsCount}
              >
                <Conditions conditions={data?.[CONDITIONS]} />
              </Section>
              {hasConditionDynamic && isDynamicVisible && (
                <Section
                  title={`Dynamic Conditions Population`}
                  isCollapsable={true}
                  initialOpen={true}
                >
                  <DynamicAdvertisingTargetDetails
                    data={data?.[DYNAMIC_CONDITIONS_TARGET]}
                  />
                </Section>
              )}
            </SidePage.NewContent>
            <SidePage.Sidebar
              actions={
                isInspection ? (
                  <Typography>Inspecting …</Typography>
                ) : (
                  <Actions
                    data={data}
                    limit={3}
                    onOpenChildComponent={openChildComponent}
                    onClose={onClose}
                    withViewLog={false}
                  />
                )
              }
            >
              <Metadata data={data} />
            </SidePage.Sidebar>
          </SidePage.NewBody>
        </SidePage.Tab>
        <SidePage.Tab label={TAB_ACTIONS_LOG}>
          <SidePage.NewBody fullHeight={false}>
            <ActionsLogFilter initialValues={filters} onSubmit={setFilters} />
          </SidePage.NewBody>
          <Divider />
          <SidePage.NewBody dense scroll={false} disableGutters={true}>
            <ActionsLog
              hasNextPage={hasNextPage}
              data={dataActionsLog}
              isLoading={isLoadingActionsLog}
              isUpdating={isUpdating}
              fetchNextPage={fetchNextPage}
              openChildComponent={openChildComponent}
              status={data?.status}
              name={data?.name}
              id={id}
              fetching={!loading && isFetchingActionsLog}
            />
          </SidePage.NewBody>
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default SmartRuleDetails;
